/**
 * The Settings tabs, and the trail back to each of them.
 *
 * One list, in one place, because two screens outside Settings now sit under
 * one of these tabs and have to name it: the checkout under Billings, the
 * booker under Support. Written twice they drift — the checkout's crumb said
 * "Billing" while the tab it led to said "Billings" — and a trail that calls
 * its destination by a name that is not on the screen it lands on is worse
 * than no trail.
 *
 * The order is the order they are drawn in.
 */

import type { Crumb } from "../../components/layout/PageHeader";
import type { TabItem } from "../../components/ui";
import { ROUTES, settingsTabPath } from "../../routes/paths";
import type { SettingsTabId } from "./types/settings.types";

export const SETTINGS_TABS: TabItem<SettingsTabId>[] = [
  { id: "general", label: "General" },
  { id: "billings", label: "Billings" },
  { id: "accounts", label: "Accounts" },
  { id: "support", label: "Support" },
  { id: "policy", label: "Policy" },
];

/** An unknown or missing `?tab=` opens on General, rather than on nothing. */
export const settingsTabFromParam = (value: string | null): SettingsTabId =>
  SETTINGS_TABS.some((tab) => tab.id === value)
    ? (value as SettingsTabId)
    : "general";

/** What a tab is called, as the tab bar itself writes it. */
export const settingsTabLabel = (tab: SettingsTabId): string =>
  SETTINGS_TABS.find((candidate) => candidate.id === tab)?.label ?? "Settings";

/**
 * The trail to a screen that sits under one of the tabs.
 *
 * Two steps, and both are needed. The tab alone was what these screens carried,
 * and it leaves the trail starting halfway: Billings and Support are not places
 * of their own — they are inside Settings, which is what the rail item, the
 * page title and the URL all say. So the trail says it too, and every screen
 * under Settings starts from the same word.
 *
 * The screen's own step goes on the end at the call site, unlinked, since it is
 * the page you are already on:
 *
 * ```tsx
 * breadcrumbs={[...settingsCrumbs("support"), { label: "Book a support call" }]}
 * ```
 */
export const settingsCrumbs = (tab: SettingsTabId): Crumb[] => [
  { label: "Settings", to: ROUTES.settings },
  { label: settingsTabLabel(tab), to: settingsTabPath(tab) },
];
